/**
 * SignIn.js — phone + OTP sign-in.
 *
 * DEFECTS IN v1 (src/pages/auth/index.js) THAT THIS FIXES
 *
 *  1. SECURITY — v1 called the callable `checkUserAndSetClaims` with the typed
 *     phone number BEFORE any OTP was sent. That endpoint created a real
 *     Firebase Auth user for whatever number an anonymous visitor typed: a free
 *     user-enumeration oracle and an unbounded way to pollute the user
 *     directory. There is no pre-OTP call here at all. Firebase creates the
 *     user on successful verification; the tenant is provisioned afterwards,
 *     by an authenticated call, in the onboarding step.
 *  2. `window.recaptchaVerifier` was a global that was created once and never
 *     `.clear()`ed, so an expired reCAPTCHA could never be rebuilt and every
 *     later send silently failed. It lives in a ref here and is cleared on
 *     unmount, on expiry and on any send error.
 *  3. A div with id `recaptcha-container` was rendered twice (plus a third,
 *     `recaptcha`), so `document.getElementById` bound the widget to whichever
 *     duplicate won. One container, one id, generated per instance.
 *  4. `window.confirmationResult` was a global dereferenced with no null guard.
 *     It is a ref, and every use checks it.
 *  5. `handleCheckUser` set isLoading(true) and never reset it on the error
 *     path — one failure left the button permanently disabled.
 *  6. `signInWithPhoneNumber` was awaited with no try/catch: an invalid number
 *     produced an unhandled rejection and a frozen UI.
 *  7. Phone was checked only with `length !== 10`, so "abcdefghij" passed.
 *     Uses validatePhone/normalisePhone.
 *  8. The OTP field had no type, no length check, no inputMode and no
 *     autoComplete, so no SMS autofill and no numeric keypad on mobile.
 *  9. A wrong OTP went to `console.log(error.message)` and the user saw
 *     nothing at all.
 * 10. The privacy-policy checkbox DEFAULTED TO TRUE, i.e. consent was
 *     pre-granted. It defaults to false and is required.
 * 11. There was no resend cooldown and no attempt limit, so the button could
 *     be hammered straight into auth/too-many-requests.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Checkbox,
  Input,
  useToast,
} from '../../components/ui';
import {
  BackIcon,
  ICON_SIZE,
  PhoneIcon,
  RefreshIcon,
  SmsIcon,
} from '../../components/ui/icons';
import { normalisePhone, validatePhone } from '../../lib/validate';

const RESEND_COOLDOWN_SECONDS = 30;
/** Firebase itself rate-limits, but stopping earlier gives a better message. */
const MAX_SEND_ATTEMPTS = 5;
const OTP_LENGTH = 6;

/* ------------------------------------------------------------------ *
 * Error mapping
 * ------------------------------------------------------------------ */

const AUTH_ERROR_MESSAGES = {
  /* --- the user did something --------------------------------------- */
  'auth/invalid-phone-number': 'That phone number is not valid. Check the 10 digits and try again.',
  'auth/missing-phone-number': 'Enter your mobile number before requesting a code.',
  'auth/invalid-verification-code': 'That code is not correct. Check the SMS and re-enter it.',
  'auth/code-expired': 'That code has expired. Request a new one.',
  'auth/missing-verification-code': 'Enter the code we sent you.',
  'auth/user-disabled': 'This number has been disabled. Contact support for help.',

  /* --- transient ---------------------------------------------------- */
  'auth/too-many-requests':
    'Too many attempts from this device. Wait a few minutes before trying again.',
  'auth/quota-exceeded': 'We cannot send any more codes right now. Please try again later.',
  'auth/network-request-failed':
    'We could not reach the server. Check your connection and try again.',
  'auth/captcha-check-failed': 'The security check failed. Reload the page and try again.',

  /**
   * --- THE PROJECT IS MISCONFIGURED ---------------------------------
   *
   * Everything below means the app is wired up wrong, not that the person
   * typing did anything wrong — and every one of them used to fall through to
   * "something went wrong", which is the least useful sentence in software.
   * They are the errors somebody bringing up a fresh Firebase project actually
   * meets, so each one names the console page that fixes it.
   */
  'auth/operation-not-allowed':
    'Phone sign-in is switched off for this project. Enable it under Authentication → Sign-in method → Phone.',
  /**
   * The one that catches everybody on a new project.
   *
   * It does NOT mean the OTP was wrong. It means the code was right, Firebase
   * went to create the account, and the project forbids creating accounts:
   * Authentication → Settings → User actions → "Enable create (sign-up)".
   * An existing user signs in fine, so it only ever shows up for the very
   * first person — which is to say, on the day you deploy.
   */
  'auth/admin-restricted-operation':
    'This project does not allow new accounts to be created. Tick "Enable create (sign-up)" under Authentication → Settings → User actions, then try again. The code you entered was correct.',
  'auth/unauthorized-domain':
    'This domain is not on the Firebase allow-list. Add it under Authentication → Settings → Authorized domains.',
  'auth/invalid-app-credential':
    'The reCAPTCHA check was rejected. Usually the site key in REACT_APP_RECAPTCHA_SITE_KEY does not belong to this project, or this domain is not on the reCAPTCHA key’s allowed list.',
  'auth/missing-app-credential':
    'No reCAPTCHA token was produced. REACT_APP_RECAPTCHA_SITE_KEY is probably empty in the build that is running.',
  'auth/app-not-authorized':
    'This app is not authorised to use this Firebase project. Check the API key restrictions in Cloud console → APIs & Services → Credentials.',
  'auth/invalid-api-key':
    'The Firebase API key is wrong. Check REACT_APP_FIREBASE_API_KEY against Project settings → General.',
  'auth/billing-not-enabled':
    'Phone sign-in on this project needs billing enabled. Upgrade the project to Blaze in the Firebase console.',
  'auth/recaptcha-not-enabled':
    'reCAPTCHA Enterprise is not enabled for this project. Enable it in Cloud console → reCAPTCHA Enterprise.',
  'auth/missing-recaptcha-token':
    'The reCAPTCHA token did not reach the server. Reload the page; if it persists, check that no extension or ad-blocker is blocking google.com/recaptcha.',
  'auth/invalid-recaptcha-token':
    'The reCAPTCHA token was rejected. The site key and the project do not match.',
  'auth/invalid-recaptcha-action':
    'reCAPTCHA rejected the action. The site key is probably a v2/v3 key rather than an Enterprise one.',
  'auth/missing-client-type':
    'The reCAPTCHA configuration is incomplete. This usually means an Enterprise site key is expected and a classic one was supplied.',
  'auth/internal-error':
    'Firebase returned an internal error. This is nearly always App Check enforcement switched on before the app was producing valid tokens — check App Check → APIs, and the request details in the browser console.',
};

/**
 * Turn a Firebase error into something a human can act on.
 *
 * THE CODE IS INCLUDED ON PURPOSE, and it is worth saying why. Ordinarily a
 * raw error code in front of a user is noise. This screen is different: it is
 * the first thing anybody sees on a freshly deployed instance, and the person
 * looking at it is usually the person who deployed it. "Something went wrong"
 * with the code swallowed sends them to the browser console at best, and to a
 * support conversation that begins with "what did it say?" at worst.
 *
 * The full error object also goes to the console, because the message is
 * capped at one sentence and the `serverResponse` on a Firebase auth error
 * often names the exact setting that is wrong.
 */
function authErrorMessage(error) {
  const code = error?.code;

  if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.error('[TruDesk] sign-in failed', {
      code,
      message: error?.message,
      customData: error?.customData,
    });
  }

  if (code && AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code];

  return code
    ? `Sign-in failed (${code}). This is a configuration problem rather than something you did — the browser console has the details.`
    : 'Something went wrong signing you in. The browser console has the details.';
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export default function SignIn() {
  const toast = useToast();

  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  // v1 initialised this to true — consent was granted before it was given.
  const [consented, setConsented] = useState(false);

  const [phoneError, setPhoneError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [consentError, setConsentError] = useState(null);
  const [formError, setFormError] = useState(null);

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);

  /* --- reCAPTCHA and confirmation, held in refs, never on window --- */

  const containerRef = useRef(null);
  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);

  // Focus is moved explicitly on each step rather than with autoFocus.
  // autoFocus fires before assistive technology has announced the page,
  // so the user can miss the context entirely; moving focus in an effect
  // after the step renders is the accessible equivalent.
  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);

  // useId can contain colons, which are legal in an id attribute but awkward
  // for selectors, so strip them. One instance, one container.
  const rawId = useId();
  const containerId = useMemo(() => `recaptcha-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`, [rawId]);

  const clearVerifier = useCallback(() => {
    // v1 never called clear(), which is why an expired widget was terminal.
    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch {
        // The widget may already be detached; nothing useful to do.
      }
      verifierRef.current = null;
    }
    if (containerRef.current) containerRef.current.innerHTML = '';
  }, []);

  const ensureVerifier = useCallback(() => {
    if (verifierRef.current) return verifierRef.current;
    verifierRef.current = new RecaptchaVerifier(auth, containerRef.current, {
      size: 'invisible',
      // Drop the expired widget so the next send builds a fresh one, instead
      // of recursing into the same cached instance the way v1 did.
      'expired-callback': () => clearVerifier(),
    });
    return verifierRef.current;
  }, [clearVerifier]);

  useEffect(() => clearVerifier, [clearVerifier]);

  useEffect(() => {
    const target = step === 'otp' ? otpInputRef.current : phoneInputRef.current;
    target?.focus();
  }, [step]);

  /* --- resend cooldown --------------------------------------------- */

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /* --- send ---------------------------------------------------------- */

  const sendOtp = useCallback(
    async (isResend) => {
      const message = validatePhone(phone);
      setPhoneError(message);
      const consentMessage = consented ? null : 'Please accept the privacy policy and terms.';
      setConsentError(consentMessage);
      if (message || consentMessage) return;

      if (attempts >= MAX_SEND_ATTEMPTS) {
        setFormError('You have requested too many codes. Please try again later.');
        return;
      }

      setSending(true);
      setFormError(null);
      try {
        const verifier = ensureVerifier();
        // No pre-flight callable. Firebase provisions the auth user only after
        // the code is actually verified.
        const confirmation = await signInWithPhoneNumber(
          auth,
          normalisePhone(phone),
          verifier
        );
        confirmationRef.current = confirmation;
        setAttempts((a) => a + 1);
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setOtp('');
        setOtpError(null);
        setStep('otp');
        toast.success(isResend ? 'A new code is on its way.' : 'We have sent you a 6-digit code.');
      } catch (error) {
        // A verifier that has already been consumed or has failed its check
        // cannot be reused — throw it away so the retry starts clean.
        clearVerifier();
        setFormError(authErrorMessage(error));
      } finally {
        // v1 only reset this on the success path.
        setSending(false);
      }
    },
    [attempts, clearVerifier, consented, ensureVerifier, phone, toast]
  );

  const handleSubmitPhone = useCallback(
    (event) => {
      event.preventDefault();
      sendOtp(false);
    },
    [sendOtp]
  );

  const handleResend = useCallback(() => {
    confirmationRef.current = null;
    clearVerifier();
    sendOtp(true);
  }, [clearVerifier, sendOtp]);

  /* --- verify -------------------------------------------------------- */

  const handleVerify = useCallback(
    async (event) => {
      event.preventDefault();

      if (!/^\d{6}$/.test(otp)) {
        setOtpError(`Enter the ${OTP_LENGTH}-digit code from the SMS.`);
        return;
      }
      if (!confirmationRef.current) {
        // v1 dereferenced window.confirmationResult with no guard at all.
        setFormError('That request has expired. Please ask for a new code.');
        return;
      }

      setVerifying(true);
      setOtpError(null);
      setFormError(null);
      try {
        await confirmationRef.current.confirm(otp);
        clearVerifier();
        confirmationRef.current = null;
        // No navigate() here: AuthContext's onIdTokenChanged sets the status
        // and the RequireAnonymous guard routes to /setup or /dashboard.
        // v1 hardcoded navigate('/billdesk/dashboard') even for a user with no
        // business, which landed them on a screen with no tenant.
      } catch (error) {
        // v1 swallowed this into console.log and showed the user nothing.
        setOtpError(authErrorMessage(error));
        setVerifying(false);
      }
    },
    [clearVerifier, otp]
  );

  const handleBack = useCallback(() => {
    setStep('phone');
    setOtp('');
    setOtpError(null);
    setFormError(null);
    confirmationRef.current = null;
    clearVerifier();
  }, [clearVerifier]);

  /* --- render -------------------------------------------------------- */

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-5)',
        backgroundColor: 'var(--bg-canvas)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div className="text-center mb-5">
          <div className="text-lg font-semibold">TruDesk</div>
          <p className="text-sm text-tertiary mt-2">GST billing, without the paperwork.</p>
        </div>

        <Card>
          <CardBody>
            {/* Exactly one reCAPTCHA container for this component instance. */}
            <div id={containerId} ref={containerRef} />

            {formError && (
              <Alert tone="danger" className="mb-4">
                {formError}
              </Alert>
            )}

            {step === 'phone' ? (
              <form onSubmit={handleSubmitPhone} noValidate>
                <h1 className="text-md font-semibold">Sign In</h1>
                <p className="text-sm text-secondary mt-2 mb-4">
                  Enter your mobile number and we will send you a one-time code.
                </p>

                <Input
                  label="Mobile Number"
                  required
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  ref={phoneInputRef}
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={phone}
                  error={phoneError}
                  hint="We will send a one-time code to this number."
                  onChange={(event) => {
                    // Digits only at the source; validatePhone is still the
                    // authority on shape (v1 only checked the length).
                    setPhone(event.target.value.replace(/\D/g, '').slice(0, 10));
                    setPhoneError(null);
                    setFormError(null);
                  }}
                />

                <div className="mt-4">
                  <Checkbox
                    label="I agree to the privacy policy and terms"
                    checked={consented}
                    onChange={(next) => {
                      setConsented(next);
                      setConsentError(null);
                    }}
                  />
                  {/* The link sits outside the <label> so that following it
                      does not also toggle the checkbox. */}
                  <div className="text-xs text-tertiary mt-2">
                    <a href="/privacy-policy" target="_blank" rel="noreferrer">
                      Read the Privacy Policy and Terms
                    </a>
                  </div>
                  {consentError && (
                    <span className="field-error" role="alert">
                      {consentError}
                    </span>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  block
                  className="mt-5"
                  loading={sending}
                  icon={PhoneIcon}
                >
                  Send Code
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerify} noValidate>
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleBack}>
                  <BackIcon size={ICON_SIZE.sm} aria-hidden="true" />
                  Change Number
                </button>

                <h1 className="text-md font-semibold mt-3">Enter Your Code</h1>
                <p className="text-sm text-secondary mt-2 mb-4">
                  We sent a {OTP_LENGTH}-digit code to {normalisePhone(phone)}.
                </p>

                <Input
                  label="One-Time Code"
                  required
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  ref={otpInputRef}
                  maxLength={OTP_LENGTH}
                  placeholder="123456"
                  value={otp}
                  error={otpError}
                  onChange={(event) => {
                    setOtp(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH));
                    setOtpError(null);
                    setFormError(null);
                  }}
                />

                <Button
                  type="submit"
                  variant="primary"
                  block
                  className="mt-5"
                  loading={verifying}
                  disabled={otp.length !== OTP_LENGTH}
                  icon={SmsIcon}
                >
                  Verify and Continue
                </Button>

                <div className="text-center mt-4">
                  {cooldown > 0 ? (
                    <span className="text-xs text-tertiary">
                      You can ask for a new code in {cooldown}s
                    </span>
                  ) : attempts >= MAX_SEND_ATTEMPTS ? (
                    <span className="text-xs text-tertiary">
                      No more codes can be sent right now. Try again later.
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={handleResend}
                      disabled={sending}
                    >
                      <RefreshIcon size={ICON_SIZE.sm} aria-hidden="true" />
                      Resend Code
                    </button>
                  )}
                  <div className="text-xs text-tertiary mt-2">
                    {attempts} of {MAX_SEND_ATTEMPTS} codes requested
                  </div>
                </div>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
